import { useState } from "react";
import { toast } from "sonner";
import { Plus, Upload, Trash2 } from "lucide-react";
import { api, useApi, errMsg, fmtDate } from "@/lib/api";
import { PageHeader, StatusBadge, DemoBadge, Card, Can, Field, LinesInput } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const EMPTY = { name: "", description: "", conditions: "", instructions: "", required_info: [], required_tools: [], examples: [], expected_output: "" };

export default function Skills() {
  const { data, reload } = useApi("/skills");
  const { data: conns } = useApi("/connections");
  const { data: priority } = useApi("/meta/priority");
  const [edit, setEdit] = useState(null);
  const save = async () => {
    try {
      if (edit.id) await api.put(`/skills/${edit.id}`, edit); else await api.post("/skills", edit);
      toast.success("Skill enregistré (brouillon)");
      setEdit(null); reload();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const act = async (fn, m) => { try { await fn(); toast.success(m); reload(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div>
      <PageHeader title="Skills" subtitle="Procédures et consignes métier réutilisables, versionnées et publiées.">
        <Can perm="skills:write"><Button onClick={() => setEdit({ ...EMPTY })} data-testid="create-skill-btn"><Plus className="h-4 w-4 mr-1" />Nouveau skill</Button></Can>
      </PageHeader>
      <Card className="mb-6">
        <h4 className="font-semibold text-sm mb-2">Ordre de priorité des règles</h4>
        <ol className="text-sm space-y-1 text-muted-foreground" data-testid="priority-order">{(priority || []).map((p) => <li key={p}>{p}</li>)}</ol>
      </Card>
      <div className="grid md:grid-cols-2 gap-4">
        {(data || []).map((s) => (
          <Card key={s.id} data-testid={`skill-card-${s.id}`} className="space-y-3">
            <div className="flex justify-between gap-2">
              <div><h3 className="font-semibold">{s.name}</h3><p className="text-xs text-muted-foreground">{s.description}</p></div>
              <div className="flex gap-1 items-start"><StatusBadge status={s.status} /><DemoBadge show={s.is_demo} /></div>
            </div>
            <div className="text-xs text-muted-foreground space-y-1">
              <div><b className="text-foreground">Conditions :</b> {s.conditions}</div>
              <div><b className="text-foreground">Outils requis :</b> {s.required_tools.map((t) => conns?.find((c) => c.id === t)?.name || t).join(", ") || "aucun"}</div>
              <div>Version publiée : {s.published ? `v${s.published.version} (${fmtDate(s.published.published_at)})` : "aucune"}</div>
            </div>
            <div className="flex gap-2 pt-2 border-t">
              <Can perm="skills:write"><Button size="sm" variant="ghost" onClick={() => setEdit(s)} data-testid={`edit-skill-${s.id}`}>Modifier</Button></Can>
              <Can perm="agents:publish"><Button size="sm" variant="outline" onClick={() => act(() => api.post(`/skills/${s.id}/publish`), "Skill publié")} data-testid={`publish-skill-${s.id}`}><Upload className="h-3.5 w-3.5 mr-1" />Publier</Button></Can>
              <Can perm="skills:write"><Button size="sm" variant="ghost" onClick={() => act(() => api.delete(`/skills/${s.id}`), "Supprimé")} data-testid={`delete-skill-${s.id}`}><Trash2 className="h-3.5 w-3.5" /></Button></Can>
            </div>
          </Card>
        ))}
      </div>
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{edit?.id ? "Modifier le skill" : "Nouveau skill"}</DialogTitle></DialogHeader>
          {edit && (
            <div className="space-y-4">
              <Field label="Nom"><Input data-testid="skill-name-input" value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} /></Field>
              <Field label="Description"><Input value={edit.description} onChange={(e) => setEdit({ ...edit, description: e.target.value })} /></Field>
              <Field label="Conditions d'utilisation"><Textarea data-testid="skill-conditions-input" value={edit.conditions} onChange={(e) => setEdit({ ...edit, conditions: e.target.value })} /></Field>
              <Field label="Instructions détaillées"><Textarea rows={6} data-testid="skill-instructions-input" value={edit.instructions} onChange={(e) => setEdit({ ...edit, instructions: e.target.value })} /></Field>
              <Field label="Informations nécessaires"><LinesInput value={edit.required_info} onChange={(v) => setEdit({ ...edit, required_info: v })} /></Field>
              <Field label="Outils requis">
                <div className="grid sm:grid-cols-2 gap-1.5">
                  {(conns || []).map((c) => (
                    <label key={c.id} className="flex items-center gap-2 text-sm"><Checkbox checked={edit.required_tools.includes(c.id)}
                      onCheckedChange={(v) => setEdit({ ...edit, required_tools: v ? [...edit.required_tools, c.id] : edit.required_tools.filter((x) => x !== c.id) })} />{c.name}</label>
                  ))}
                </div>
              </Field>
              <Field label="Résultat attendu"><Textarea value={edit.expected_output} onChange={(e) => setEdit({ ...edit, expected_output: e.target.value })} /></Field>
            </div>
          )}
          <DialogFooter><Button onClick={save} data-testid="save-skill-btn">Enregistrer</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
